import { FilterCriteria, humanOperatorToOperator } from '../../constants';
import { Suggestion, TokenizerResult, TokenType } from './types';
import { checkIsBooleanField } from '../../utils';
import { FilterTokenizer } from './FilterTokenizer';

// Add interface for shape data - matches ShapeSelectType
interface ShapeData {
  id: string | undefined;
  name: string | undefined;
  type: string;
  geometryType: string; // Add geometryType property
}

export class SuggestionEngine {
  constructor(
    private filterOptions: Record<string, FilterCriteria>,
    private shapeData?: ShapeData[] // Optional shape data
  ) {}

  getSuggestions(
    tokenInfo: TokenizerResult,
    searchTerm: string = '',
    input: string = '',
    cursorPosition: number = 0
  ): Suggestion[] {
    const { tokens, expectedNext, currentToken, cursorContext } = tokenInfo;

    // Determine what we're searching for
    let actualSearchTerm = searchTerm;

    // If we're editing a token, use its partial value
    if (currentToken && cursorContext?.insideToken) {
      if (currentToken.type === 'value' && currentToken.raw.startsWith('"')) {
        // For quoted values, adjust for the opening quote
        const positionInValue = cursorContext.positionInToken - 1; // -1 for opening quote
        actualSearchTerm = currentToken.value.substring(
          0,
          Math.max(0, positionInValue)
        );
      } else {
        actualSearchTerm = currentToken.value.substring(
          0,
          cursorContext.positionInToken
        );
      }
    } else if (currentToken) {
      actualSearchTerm = currentToken.value;
    }

    // Special handling for incomplete quoted strings when no current token exists
    if (!currentToken && expectedNext === TokenType.Value && input) {
      // Use the centralized quote detection from FilterTokenizer
      const incompleteQuoteContent =
        FilterTokenizer.extractIncompleteQuoteContent(input, cursorPosition);
      if (incompleteQuoteContent !== null) {
        actualSearchTerm = incompleteQuoteContent;
      }
    }

    // Get the last field token for context
    const lastField = tokens
      .filter((t) => t.type === 'field')
      .pop()
      ?.value?.toUpperCase();

    // Check if the last field is boolean
    const isLastFieldBoolean =
      lastField &&
      this.filterOptions[lastField] &&
      checkIsBooleanField(this.filterOptions[lastField].data_types);

    // Check what type of token we're currently editing
    const editingTokenType = currentToken?.type;

    // If we're editing an existing token, use its type to determine suggestions
    if (editingTokenType === 'field') {
      return this.getFieldSuggestions(actualSearchTerm);
    } else if (editingTokenType === 'operator') {
      if (!lastField || isLastFieldBoolean) return [];
      return this.getOperatorSuggestions(lastField, actualSearchTerm);
    } else if (editingTokenType === 'value') {
      if (!lastField || isLastFieldBoolean) return [];
      return this.getValueSuggestions(lastField, actualSearchTerm);
    } else if (editingTokenType === 'logical') {
      return this.getLogicalSuggestions(actualSearchTerm);
    }

    // Otherwise use the expected next type, but override for boolean fields
    switch (expectedNext) {
      case TokenType.Field:
        return this.getFieldSuggestions(actualSearchTerm);

      case TokenType.ArithmeticOperator:
        if (isLastFieldBoolean) {
          return this.getLogicalSuggestions(actualSearchTerm);
        }
        if (!lastField) return [];
        return this.getOperatorSuggestions(lastField, actualSearchTerm);

      case TokenType.Value:
        if (isLastFieldBoolean) {
          return this.getLogicalSuggestions(actualSearchTerm);
        }
        if (!lastField) return [];
        return this.getValueSuggestions(lastField, actualSearchTerm);

      case TokenType.LogicalOperator:
        return this.getLogicalSuggestions(actualSearchTerm);

      default:
        return [];
    }
  }

  private getFieldSuggestions(searchTerm: string): Suggestion[] {
    const lowerSearchTerm = searchTerm.toLowerCase();

    return Object.keys(this.filterOptions)
      .filter((field) => {
        return field.toLowerCase().includes(lowerSearchTerm);
      })
      .sort((a, b) => {
        const aStartsWith = a.toLowerCase().startsWith(lowerSearchTerm);
        const bStartsWith = b.toLowerCase().startsWith(lowerSearchTerm);

        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        return a.localeCompare(b);
      })
      .map((field) => ({
        type: 'field' as const,
        value: field.toLowerCase(),
        label: field.toLowerCase(),
        icon: this.filterOptions[field].icon,
        description: `Type: ${this.filterOptions[field].data_types.join(', ')}`,
      }));
  }

  private getOperatorSuggestions(
    field: string,
    searchTerm: string
  ): Suggestion[] {
    const fieldConfig = this.filterOptions[field];
    if (!fieldConfig) return [];

    const lowerSearchTerm = searchTerm.toLowerCase();
    const suggestions: Suggestion[] = [];

    fieldConfig.operators.forEach((humanOp) => {
      const symbol = humanOperatorToOperator(humanOp);

      const matchesSymbol = symbol.toLowerCase().includes(lowerSearchTerm);
      const matchesHuman = humanOp.toLowerCase().includes(lowerSearchTerm);

      if (matchesSymbol || matchesHuman) {
        // Handle semantic operators with special syntax
        if (humanOp === 'contains' && symbol === '=') {
          suggestions.push({
            type: 'operator' as const,
            value: '= "*|*"',
            label: 'contains',
            icon: null,
            description: '= "*...*"',
          });
        } else if (humanOp === 'starts with' && symbol === '=') {
          suggestions.push({
            type: 'operator' as const,
            value: '= "|*"',
            label: 'starts with',
            icon: null,
            description: '= "...*"',
          });
        } else if (humanOp === 'ends with' && symbol === '=') {
          suggestions.push({
            type: 'operator' as const,
            value: '= "*|"',
            label: 'ends with',
            icon: null,
            description: '= "*..."',
          });
        } else if (humanOp === 'does not contain' && symbol === '!=') {
          suggestions.push({
            type: 'operator' as const,
            value: '!= "*|*"',
            label: 'does not contain',
            icon: null,
            description: '!= "*...*"',
          });
        } else if (humanOp === 'does not start with' && symbol === '!=') {
          suggestions.push({
            type: 'operator' as const,
            value: '!= "|*"',
            label: 'does not start with',
            icon: null,
            description: '!= "...*"',
          });
        } else if (humanOp === 'does not end with' && symbol === '!=') {
          suggestions.push({
            type: 'operator' as const,
            value: '!= "*|"',
            label: 'does not end with',
            icon: null,
            description: '!= "*..."',
          });
        } else {
          suggestions.push({
            type: 'operator' as const,
            value: symbol,
            label: humanOp,
            icon: null,
            description: symbol,
          });
        }
      }
    });

    return suggestions;
  }

  private getValueSuggestions(field: string, searchTerm: string): Suggestion[] {
    const upperField = field.toUpperCase();
    const fieldConfig = this.filterOptions[upperField];

    if (!fieldConfig) {
      return [];
    }

    // Special handling for position fields - use shape data if available
    const fieldLower = field.toLowerCase();
    if (fieldLower === 'position') {
      return this.getShapeSuggestions(searchTerm, ['Polygon']);
    }

    if (fieldLower === 'circle_position') {
      return this.getShapeSuggestions(searchTerm, ['Circle']);
    }

    // For all other fields, proceed with normal value suggestions (no shape processing)
    // If we have predefined values, use them
    if (fieldConfig.values && fieldConfig.values.length > 0) {
      const lowerSearchTerm = searchTerm.toLowerCase();

      const realSuggestions = fieldConfig.values
        .filter((val) => {
          return String(val).toLowerCase().includes(lowerSearchTerm);
        })
        .sort((a, b) => {
          const aStr = String(a).toLowerCase();
          const bStr = String(b).toLowerCase();
          const aStartsWith = aStr.startsWith(lowerSearchTerm);
          const bStartsWith = bStr.startsWith(lowerSearchTerm);

          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;

          return aStr.localeCompare(bStr);
        })
        .map((val) => ({
          type: 'value' as const,
          value: String(val),
          label: String(val),
          icon: null,
          description: null,
        }));

      console.log(
        'seartchTerm',
        searchTerm,
        'realSuggestions',
        realSuggestions
      );

      // If we have a search term but no matches, return empty array (not hints)
      // Only show hints when there's no search term
      if (realSuggestions.length === 0 && searchTerm.trim().length > 0) {
        return []; // No matches for search - don't show hints
      }

      // If we have real suggestions, return them
      if (realSuggestions.length > 0) {
        return realSuggestions;
      }

      // No search term and no suggestions - show hints only if no search term
      if (searchTerm.trim().length === 0) {
        return this.getInputHints(upperField, fieldConfig);
      }

      return []; // Has search term but no matches
    }

    return this.getInputHints(upperField, fieldConfig);
  }

  private getShapeSuggestions(
    searchTerm: string,
    allowedGeometryTypes: string[]
  ): Suggestion[] {
    // If no shape data is available, return hints
    if (!this.shapeData || this.shapeData.length === 0) {
      if (searchTerm.trim().length === 0) {
        const geometryType = allowedGeometryTypes[0]?.toLowerCase();
        const label =
          geometryType === 'polygon'
            ? 'Select a polygon...'
            : 'Select a circle...';
        const description =
          geometryType === 'polygon'
            ? 'Choose a polygon area from available shapes'
            : 'Choose a circular area from available shapes';

        return [
          {
            type: 'value' as const,
            value: '',
            label,
            icon: null,
            description,
            isHint: true,
          },
        ];
      }
      return []; // No suggestions when searching without data
    }

    // Filter shapes by geometry type and search term
    const lowerSearchTerm = searchTerm.toLowerCase();
    const lowerAllowedTypes = allowedGeometryTypes.map((type) =>
      type.toLowerCase()
    );

    const filteredShapes = this.shapeData.filter((shape) => {
      // Skip shapes without valid id or name
      if (!shape.id || !shape.name) {
        return false;
      }

      // Filter by geometry type if specified (case-insensitive comparison)
      if (allowedGeometryTypes.length > 0 && shape.geometryType) {
        if (!lowerAllowedTypes.includes(shape.geometryType.toLowerCase())) {
          return false;
        }
      }

      // Filter by search term
      if (searchTerm.trim().length > 0) {
        return shape.name.toLowerCase().includes(lowerSearchTerm);
      }

      return true;
    });

    return filteredShapes.map((shape) => ({
      type: 'value' as const,
      value: shape.id!,
      label: shape.name!,
      icon: null,
      description: shape.geometryType ? `${shape.geometryType} shape` : 'Shape',
    }));
  }

  private getInputHints(
    field: string,
    fieldConfig: FilterCriteria
  ): Suggestion[] {
    const hints: Suggestion[] = [];

    // Field-specific hints based on field name patterns
    const fieldLower = field.toLowerCase();

    if (fieldLower.includes('name')) {
      hints.push({
        type: 'value' as const,
        value: '',
        label: 'Enter name...',
        icon: null,
        description: 'Type any name (e.g., "Enterprise", "Titanic")',
        isHint: true,
      });
    } else if (fieldLower.includes('id') || fieldLower.includes('identifier')) {
      hints.push({
        type: 'value' as const,
        value: '',
        label: 'Enter ID...',
        icon: null,
        description: 'Type an identifier (e.g., "12345", "ABC-123")',
        isHint: true,
      });
    } else if (fieldLower.includes('email')) {
      hints.push({
        type: 'value' as const,
        value: '',
        label: 'Enter email...',
        icon: null,
        description: 'Type an email address (e.g., "user@domain.com")',
        isHint: true,
      });
    } else if (fieldLower.includes('phone')) {
      hints.push({
        type: 'value' as const,
        value: '',
        label: 'Enter phone number...',
        icon: null,
        description: 'Type a phone number (e.g., "+1234567890")',
        isHint: true,
      });
    } else if (fieldLower.includes('url') || fieldLower.includes('link')) {
      hints.push({
        type: 'value' as const,
        value: '',
        label: 'Enter URL...',
        icon: null,
        description: 'Type a URL (e.g., "https://example.com")',
        isHint: true,
      });
    } else if (fieldLower.includes('code')) {
      hints.push({
        type: 'value' as const,
        value: '',
        label: 'Enter code...',
        icon: null,
        description: 'Type a code (e.g., "ABC123", "XYZ-789")',
        isHint: true,
      });
    }

    // Data type-specific hints
    fieldConfig.data_types.forEach((dataType) => {
      const typeLower = dataType.toLowerCase();

      if (typeLower.includes('string') || typeLower.includes('text')) {
        if (hints.length === 0) {
          // Only add if no field-specific hints
          hints.push({
            type: 'value' as const,
            value: '',
            label: 'Enter text...',
            icon: null,
            description: 'Type any text value',
            isHint: true,
          });
        }
      } else if (typeLower.includes('number') || typeLower.includes('int')) {
        hints.push({
          type: 'value' as const,
          value: '',
          label: 'Enter number...',
          icon: null,
          description: 'Type a numeric value (e.g., 123, 45.67)',
          isHint: true,
        });
      } else if (typeLower.includes('date')) {
        hints.push({
          type: 'value' as const,
          value: '',
          label: 'Enter date...',
          icon: null,
          description: 'Type a date (e.g., "2024-01-15", "2024/01/15")',
          isHint: true,
        });
      }
    });

    // Add common wildcard patterns if applicable
    if (
      fieldConfig.operators.some((op) =>
        ['contains', 'starts with', 'ends with'].includes(op)
      )
    ) {
      const typeExamples: Record<string, string> = {
        string:
          'Wrap patterns in quotes and use * as a wildcard. Example: "*den*" (contains "den"), "den*" (starts with "den"), "*mark" (ends with "mark").',
        number:
          'Wrap patterns in quotes and use * as a wildcard. Example: "*219*" (contains 219), "219*" (starts with 219), "*219" (ends with 219).',
      };

      fieldConfig.data_types.forEach((dataType) => {
        const typeLower = dataType.toLowerCase();

        if (typeLower.includes('string') || typeLower.includes('text')) {
          hints.push({
            type: 'value' as const,
            value: '',
            label: 'Wildcard',
            icon: null,
            description: typeExamples.string,
            isHint: true,
          });
        } else if (typeLower.includes('number') || typeLower.includes('int')) {
          hints.push({
            type: 'value' as const,
            value: '',
            label: 'Wildcard',
            icon: null,
            description: typeExamples.number,
            isHint: true,
          });
        }
      });
    }

    return hints;
  }

  private getLogicalSuggestions(searchTerm: string): Suggestion[] {
    const lowerSearchTerm = searchTerm.toLowerCase();

    return ['AND', 'OR']
      .filter((op) => op.toLowerCase().includes(lowerSearchTerm))
      .map((op) => ({
        type: 'logical' as const,
        value: op,
        label: op.toLowerCase(),
        icon: null,
        description: 'Logical operator',
      }));
  }
}
